import { Button } from "@/components/ui/button";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useFacilities } from "@/hooks/useFacilities";
import { useDispatch } from "react-redux";
import { translateWithMultipleSources } from "@/lib/translate";
import { AppLocation, FunnelType, QuoteType, Step, WidgetLayout } from "@/types/enums";
import { emitUnitEvent } from "@/utils/units/unitEvents";
import { initializeJourney } from "@/features/journey/journeySlice";
import { UnitGroup } from "@/types";
import { getSteps } from "@/hooks/useSteps";
import { useContext } from "react";
import { UnitItemContext } from "@/context/UnitItemContext";

interface UnitQuoteButtonProps {
    unitGroup: UnitGroup;
    selectedPromo: number;
    // Skip the Layout 2 in-row toggle and always go to the standalone page
    // journey. Used by the quote button rendered inside the promo selector and
    // the rent/reserve/quote checkout modal, where there is no row to expand.
    navigateToPage?: boolean;
}

export const QuoteButton = ({
    unitGroup,
    selectedPromo,
    navigateToPage = false,
}: UnitQuoteButtonProps) => {
    const dispatch = useDispatch();
    const options = useDisplayOptionContext();
    const { facilities } = useFacilities();
    // navigateToPage callers (the promo selector, the checkout modal) can render
    // outside a unit row, so read the context softly rather than requiring a
    // provider. The in-row toggle path is only taken when it IS present.
    const itemContext = useContext(UnitItemContext);
    const promoExpandedBy = itemContext?.promoExpandedBy ?? null;
    const setPromoExpandedBy = itemContext?.setPromoExpandedBy;
    const promoExpandedPanelId = itemContext?.promoExpandedPanelId;

    // Both layouts expand the row in place (Layout 1: WEB-1432, Layout 2: WEB-1436).
    const inlineExpansionEnabled =
        options.widgetLayout === WidgetLayout.LAYOUT_1 ||
        options.widgetLayout === WidgetLayout.LAYOUT_2;
    const isQuoteExpanded = promoExpandedBy === "quote";
    // The in-row Get a Quote toggles the expanded row open: a single promo shows
    // the inline quote form, multiple promos show the promo selector (UnitItem
    // picks which). navigateToPage callers opt out of the toggle.
    const isRowToggle = inlineExpansionEnabled && !navigateToPage;

    const unitId = unitGroup.representative_unit.id;
    const facilityId = unitGroup.representative_unit.facility_id;

    const facilityInfo = facilities.find(
        (facility) => facility.id === facilityId,
    );

    const handleQuoteEvent = () => {
        emitUnitEvent(
            unitId,
            unitGroup.representative_unit.facility_id,
            selectedPromo,
            "inquiry",
        );
    };

    const handleExternalSoftReservation = () => {
        if (facilityInfo && options.softReservationApp?.externalUrl) {
            const translatedUrl = translateWithMultipleSources(
                options.softReservationApp.externalUrl,
                {
                    unitGroup,
                    facility: facilityInfo,
                },
                Number(unitId),
                Number(selectedPromo),
            );
            window.location.href = translatedUrl;
        }
    };

    const activateSoftReservationJourney = () => {
        const subGroupCount = unitGroup.sub_groups?.length ?? 0;
        const steps = getSteps(subGroupCount, options.buttonLayout);

        dispatch(
            initializeJourney({
                unitId,
                unitGroup,
                selectedPromo,
                stepList: steps,
                step: Step.FUNNEL,
                funnelType: FunnelType.QUOTE,
            }),
        );
    };

    const handleSoftReserveClick = () => {
        handleQuoteEvent();

        // In-row: toggle the expanded row (inline form for a single promo,
        // promo selector for multiple — decided by UnitItem).
        if (isRowToggle && setPromoExpandedBy) {
            setPromoExpandedBy(isQuoteExpanded ? null : "quote");
            return;
        }

        if (
            options.softReservationApp?.location === AppLocation.EXTERNAL &&
            options.softReservationApp?.externalUrl
        ) {
            handleExternalSoftReservation();
        } else {
            activateSoftReservationJourney();
        }
    };

    if (
        options.disableSoftReservation &&
        facilityInfo?.settings?.quote_type !== QuoteType.RESERVE
    )
        return null;

    return (
        <Button
            variant="secondary"
            data-testid="quoteButton"
            onClick={handleSoftReserveClick}
            className="soft-reservation-btn"
            aria-expanded={isRowToggle ? isQuoteExpanded : undefined}
            aria-controls={isRowToggle ? promoExpandedPanelId : undefined}
        >
            {options.language?.action?.quoteButton}
        </Button>
    );
};

QuoteButton.displayName = "QuoteButton";
